import React, { useState, useEffect, useRef } from 'react';
import { UploadCloud, X, ArrowRight, Building2, Briefcase, FileAudio, Trash2, LayoutGrid, CheckCircle2, Clock, Coins, Globe, Sparkles, AlertCircle, Zap, Loader2 } from 'lucide-react';
import { useLanguage } from '../i18nContext';
import { canCreateTask, getRecommendedAsrMode, calculateAudioMinutes } from '../services/membership';
import { uploadFile, UploadResult } from '../services/file';
import { needsConversion, convertToMp3 } from '../services/audioConverter';
import { CanCreateTaskResponse, RecommendedAsrModeResponse } from '../types';

interface NewRecordingModalProps {
  onClose: () => void;
  onStart: (params: {
    scene: string;
    fileId?: string;
    industry?: string;
    company?: string;
    context?: string;
  }) => void;
  onUpgrade?: () => void;
}

export default function NewRecordingModal({ onClose, onStart, onUpgrade }: NewRecordingModalProps) {
  const { t, language } = useLanguage();
  const [selectedScenario, setSelectedScenario] = useState('finance');
  const [file, setFile] = useState<File | null>(null);
  const [fileId, setFileId] = useState<string | null>(null);
  const [fileDuration, setFileDuration] = useState<number | null>(null);
  const [textLength, setTextLength] = useState<number | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [convertingMsg, setConvertingMsg] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  // 输入框状态
  const [industry, setIndustry] = useState('');
  const [company, setCompany] = useState('');
  const [content, setContent] = useState('');

  // 任务创建限制检查
  const [canCreate, setCanCreate] = useState<CanCreateTaskResponse | null>(null);
  const [asrModeInfo, setAsrModeInfo] = useState<RecommendedAsrModeResponse | null>(null);
  const [checkingLimit, setCheckingLimit] = useState(true);

  // 加载任务创建限制和ASR模式
  useEffect(() => {
    const loadLimits = async () => {
      setCheckingLimit(true);
      try {
        const [canCreateRes, asrModeRes] = await Promise.all([
          canCreateTask(),
          getRecommendedAsrMode(),
        ]);
        setCanCreate(canCreateRes);
        setAsrModeInfo(asrModeRes);
      } catch (err) {
        // 如果获取失败，默认允许创建
        setCanCreate({ canCreate: true });
      } finally {
        setCheckingLimit(false);
      }
    };
    loadLimits();
  }, []);

  // Get dynamic field labels based on selected scenario
  const contextFields = t.newModal.contextInputs[selectedScenario as keyof typeof t.newModal.contextInputs] || t.newModal.contextInputs.podcast;

  // 真实文件选择
  const handleFileSelect = () => {
    fileInputRef.current?.click();
  };

  // 文件类型校验（点击选择和拖拽共用）
  const audioVideoTypes = ['audio/mpeg', 'audio/mp3', 'audio/wav', 'audio/m4a', 'audio/ogg', 'audio/flac', 'video/mp4'];
  const documentTypes = [
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
    'text/plain', // .txt
    'application/pdf', // .pdf
    'text/markdown', // .md
  ];
  const allAllowedTypes = [...audioVideoTypes, ...documentTypes];
  const audioVideoExtensions = /\.(mp3|wav|m4a|ogg|flac|mp4)$/i;
  const documentExtensions = /\.(docx|txt|pdf|md)$/i;

  const validateAndUpload = async (selectedFile: File) => {
    if (!allAllowedTypes.includes(selectedFile.type) &&
        !selectedFile.name.match(audioVideoExtensions) &&
        !selectedFile.name.match(documentExtensions)) {
      setUploadError(language === 'zh' ? '不支持的文件格式，请上传 MP3/WAV/M4A/OGG/FLAC/MP4/DOCX/TXT/PDF/MD 格式' : 'Unsupported format. Please upload MP3/WAV/M4A/OGG/FLAC/MP4/DOCX/TXT/PDF/MD');
      return;
    }

    if (selectedFile.size > 500 * 1024 * 1024) {
      setUploadError(language === 'zh' ? '文件大小不能超过 500MB' : 'File size cannot exceed 500MB');
      return;
    }

    setFile(selectedFile);
    setUploadError(null);
    setUploading(true);
    setConvertingMsg(null);

    try {
      let fileToUpload = selectedFile;
      /*
      if (needsConversion(selectedFile)) {
        console.log('[upload] converting:', selectedFile.name, selectedFile.type);
        setConvertingMsg(language === 'zh' ? '计算中...' : 'Calculating...');
        fileToUpload = await convertToMp3(selectedFile, () => {});
        console.log('[upload] converted to:', fileToUpload.name, fileToUpload.size);
        setConvertingMsg(language === 'zh' ? '上传中...' : 'Uploading...');
        setFile(fileToUpload);
      }
      */

      const result = await uploadFile(fileToUpload);
      setConvertingMsg(null);
      setFileId(result.fileId);
      setFileDuration(result.durationSeconds || null);
      setTextLength(result.textLength || null);
    } catch (err: any) {
      console.error('Upload failed:', err);
      setUploadError(err.message || (language === 'zh' ? '文件上传失败' : 'File upload failed'));
      setFile(null);
    } finally {
      setUploading(false);
    }
  };

  // 处理文件选择变化
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;
    await validateAndUpload(selectedFile);
  };

  // 拖拽事件处理
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const droppedFile = e.dataTransfer.files?.[0];
    if (!droppedFile) return;
    await validateAndUpload(droppedFile);
  };

  const clearFile = (e: React.MouseEvent) => {
      e.stopPropagation();
      setFile(null);
      setFileId(null);
      setFileDuration(null);
      setTextLength(null);
      setUploadError(null);
      setConvertingMsg(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
  };

  // 格式化文件大小
  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Scenarios Configuration
  const scenarios = [
      { id: 'finance', ...t.newModal.scenarios.finance, color: 'bg-blue-500', theme: 'blue' },
      { id: 'podcast', ...t.newModal.scenarios.podcast, color: 'bg-emerald-500', theme: 'emerald' },
      { id: 'qa', ...t.newModal.scenarios.qa, color: 'bg-amber-500', theme: 'amber' },
      { id: 'course', ...t.newModal.scenarios.course, color: 'bg-purple-500', theme: 'purple' },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 font-sans text-brand-900">
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm transition-opacity duration-300"
        onClick={onClose}
      ></div>

      {/* --- Fixed Height Modal (760px) --- */}
      <div className="relative w-full max-w-6xl bg-surface-light rounded-pricing shadow-2xl overflow-hidden flex flex-col md:flex-row border border-surface-border h-[760px]">
        
        {/* === LEFT COLUMN: Template Selection (Expanded to fill space) === */}
        <div className="flex-1 bg-surface p-12 overflow-hidden relative flex flex-col border-r border-surface-border">
            <div className="mb-10">
                <h2 className="text-3xl font-serif font-bold text-brand-900 mb-2">{t.newModal.title}</h2>
                <p className="text-ink-secondary text-sm font-medium opacity-80">{t.newModal.subtitle}</p>
            </div>

            <div className="flex-1 flex flex-col">
                <h3 className="text-[10px] font-bold text-ink-muted mb-6 uppercase tracking-[0.2em] pl-1 flex items-center gap-2 h-4">
                    <span className="w-1.5 h-1.5 rounded-full bg-ink-muted"></span>
                    {t.newModal.scenarioTitle}
                </h3>
                
                {/* Unified Gap and Grid - Using rows to fill space */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 flex-1 items-stretch">
                    {scenarios.map((s) => (
                        <RichScenarioCard 
                            key={s.id}
                            title={titleMap(s.id, language === 'zh' ? 'zh' : 'en')}
                            subtitle={s.sub}
                            description={s.desc}
                            color={s.color}
                            theme={s.theme}
                            selected={selectedScenario === s.id}
                            onClick={() => setSelectedScenario(s.id)}
                        />
                    ))}
                </div>
            </div>

            <div className="pt-6 border-t border-surface-border/60 text-center mt-8">
                <p className="text-ink-muted text-[10px] font-bold uppercase tracking-widest flex items-center justify-center gap-2">
                    <Sparkles size={12} fill="currentColor" />
                    {t.newModal.comingSoon}
                </p>
            </div>
        </div>

        {/* === RIGHT COLUMN: Upload & Context === */}
        <div className="w-full md:w-[420px] bg-white flex flex-col relative shrink-0">
            
            <div className="absolute top-8 right-8 z-20">
                <button onClick={onClose} className="p-2 text-ink-muted hover:text-brand-900 rounded-full transition-colors hover:bg-surface">
                    <X size={24} />
                </button>
            </div>

            {/* Precise Alignment with "SELECT SCENARIO" header baseline */}
            <div className="pt-[144px] px-10 shrink-0">
                <h3 className="text-[10px] font-bold text-ink-muted uppercase tracking-[0.2em] mb-6 h-4">
                    {t.newModal.sourceFile}
                </h3>
            </div>

            {/* Main Content Area */}
            <div className={`flex-1 overflow-y-auto px-10 flex flex-col custom-scrollbar ${!file ? 'justify-start pt-2' : 'justify-start'}`}>
                {/* 隐藏的文件输入 */}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="audio/*,video/mp4,.mp3,.wav,.m4a,.ogg,.flac,.mp4,.docx,.txt,.pdf,.md,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain,application/pdf,text/markdown"
                  onChange={handleFileChange}
                  className="hidden"
                />
                <div className="relative w-full">
                    {!file ? (
                        <div
                            onClick={handleFileSelect}
                            onDragOver={handleDragOver}
                            onDragEnter={handleDragEnter}
                            onDragLeave={handleDragLeave}
                            onDrop={handleDrop}
                            className={`w-full aspect-[4/3] border-2 border-dashed rounded-modal flex flex-col items-center justify-center transition-all duration-300 cursor-pointer group shadow-sm
                              ${isDragging
                                ? 'border-brand-700 bg-brand-700/5 scale-[1.02]'
                                : 'border-surface-divider bg-surface hover:bg-surface-border/40 hover:border-brand-700/50'}`}
                        >
                            <div className="w-16 h-16 bg-white border border-surface-border rounded-2xl shadow-sm flex items-center justify-center text-ink-muted group-hover:text-brand-700 group-hover:scale-110 transition-all duration-500 mb-5">
                                <UploadCloud size={32} />
                            </div>
                            <p className="text-brand-900 font-bold text-xl">{t.newModal.upload}</p>
                            <p className="text-sm text-ink-muted mt-1">{t.newModal.dragDrop}</p>
                            <p className="text-[10px] text-ink-muted/60 mt-5 font-bold uppercase tracking-wider">{t.newModal.fileLimit}</p>
                            {uploadError && (
                              <p className="text-xs text-red-500 mt-2">{uploadError}</p>
                            )}
                        </div>
                    ) : (
                        <div className="space-y-6">
                            {/* File Card & Cost */}
                            <div className="space-y-3">
                                <div className="w-full border border-surface-divider bg-surface rounded-3xl p-4 relative group shadow-sm">
                                    <div className="flex items-start gap-4">
                                        <div className="w-12 h-12 bg-white rounded-xl border border-surface-border shadow-sm flex items-center justify-center text-brand-700 shrink-0">
                                            {uploading ? <Loader2 size={24} className="animate-spin" /> : <FileAudio size={24} />}
                                        </div>
                                        <div className="flex-1 min-w-0 pr-8">
                                            <div className="text-sm font-bold text-brand-900 font-serif truncate leading-tight mb-1">{file.name}</div>
                                            <div className="text-[11px] text-ink-secondary font-bold uppercase tracking-wide opacity-70">
                                              {formatFileSize(file.size)}
                                              {uploading && convertingMsg && <span className="ml-2 text-ink-muted">{convertingMsg}</span>}
                                              {uploading && !convertingMsg && <span className="ml-2 text-ink-muted">{language === 'zh' ? '上传中...' : 'Uploading...'}</span>}
                                              {!uploading && fileId && <span className="ml-2 text-brand-700">{language === 'zh' ? '已上传' : 'Uploaded'}</span>}
                                            </div>
                                        </div>
                                        <button onClick={clearFile} disabled={uploading} className="absolute top-4 right-4 text-ink-muted hover:text-red-500 transition-colors p-1 disabled:opacity-50">
                                            <Trash2 size={16} />
                                        </button>
                                    </div>
                                </div>

                                <div className="bg-surface-border/40 rounded-2xl p-4 border border-surface-border flex items-center justify-between shadow-inner">
                                    <div className="flex items-center gap-3">
                                        <div className="w-8 h-8 rounded-lg bg-brand-900 flex items-center justify-center text-surface-border shadow-md">
                                            <Clock size={14} />
                                        </div>
                                        <div>
                                            <div className="text-[9px] font-bold text-ink-muted uppercase tracking-wide leading-none mb-1">{t.newModal.estimatedCost}</div>
                                            <div className="text-xs font-bold text-brand-900">
                                              {fileDuration && fileDuration > 0
                                                ? `~${Math.ceil(fileDuration / 60)} min`
                                                : textLength && textLength > 0
                                                  ? `~${Math.ceil(textLength / 500)} min`
                                                  : (uploading ? (language === 'zh' ? '计算中...' : 'Calculating...') : '--')}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <div className="text-[9px] font-bold text-ink-muted uppercase tracking-wide leading-none mb-1">{t.newModal.usageDeduction}</div>
                                        <div className="text-xs font-bold text-brand-700">
                                          {fileDuration && fileDuration > 0
                                            ? `-${Math.ceil(fileDuration / 60)} ${t.newModal.deductionUnit}`
                                            : textLength && textLength > 0
                                              ? `-${Math.ceil(textLength / 500)} ${t.newModal.deductionUnit}`
                                              : (uploading ? (language === 'zh' ? '计算中...' : 'Calculating...') : '--')}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Context Details */}
                            <div className="space-y-5 pt-2">
                                <div className="flex items-center gap-4">
                                    <div className="h-px bg-surface-border flex-1"></div>
                                    <h3 className="text-[10px] font-bold text-ink-muted uppercase tracking-[0.2em]">{t.newModal.contextTitle}</h3>
                                    <div className="h-px bg-surface-border flex-1"></div>
                                </div>

                                {/* 金融访谈模板：3个输入框（行业、公司名称、内容） */}
                                {selectedScenario === 'finance' ? (
                                    <>
                                        <div className="space-y-1.5">
                                            <label className="text-[10px] font-bold text-ink-dark uppercase tracking-widest flex items-center gap-2 ml-1">
                                                <Briefcase size={12} className="text-ink-muted"/> {contextFields.label1}
                                            </label>
                                            <input
                                                type="text"
                                                value={industry}
                                                onChange={(e) => setIndustry(e.target.value)}
                                                placeholder={contextFields.ph1}
                                                className="w-full h-11 px-4 rounded-xl border border-surface-border bg-surface text-sm text-brand-900 focus:bg-white focus:border-brand-900 focus:ring-4 focus:ring-brand-900/5 transition-all outline-none shadow-sm"
                                            />
                                        </div>

                                        <div className="space-y-1.5">
                                            <label className="text-[10px] font-bold text-ink-dark uppercase tracking-widest flex items-center gap-2 ml-1">
                                                <Building2 size={12} className="text-ink-muted" /> {(contextFields as any).label2}
                                            </label>
                                            <input
                                                type="text"
                                                value={company}
                                                onChange={(e) => setCompany(e.target.value)}
                                                placeholder={(contextFields as any).ph2}
                                                className="w-full h-11 px-4 rounded-xl border border-surface-border bg-surface text-sm text-brand-900 focus:bg-white focus:border-brand-900 focus:ring-4 focus:ring-brand-900/5 transition-all outline-none shadow-sm"
                                            />
                                        </div>

                                        <div className="space-y-1.5">
                                            <label className="text-[10px] font-bold text-ink-dark uppercase tracking-widest flex items-center gap-2 ml-1">
                                                <Globe size={12} className="text-ink-muted" /> {(contextFields as any).label3}
                                            </label>
                                            <input
                                                type="text"
                                                value={content}
                                                onChange={(e) => setContent(e.target.value)}
                                                placeholder={(contextFields as any).ph3}
                                                className="w-full h-11 px-4 rounded-xl border border-surface-border bg-surface text-sm text-brand-900 focus:bg-white focus:border-brand-900 focus:ring-4 focus:ring-brand-900/5 transition-all outline-none shadow-sm"
                                            />
                                        </div>
                                    </>
                                ) : (
                                    /* 其他模板：1个输入框（内容） */
                                    <div className="space-y-1.5">
                                        <label className="text-[10px] font-bold text-ink-dark uppercase tracking-widest flex items-center gap-2 ml-1">
                                            <Building2 size={12} className="text-ink-muted"/> {contextFields.label1}
                                        </label>
                                        <input
                                            type="text"
                                            value={content}
                                            onChange={(e) => setContent(e.target.value)}
                                            placeholder={contextFields.ph1}
                                            className="w-full h-11 px-4 rounded-xl border border-surface-border bg-surface text-sm text-brand-900 focus:bg-white focus:border-brand-900 focus:ring-4 focus:ring-brand-900/5 transition-all outline-none shadow-sm"
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Bottom Actions */}
            <div className="p-10 pt-6 border-t border-surface-border bg-surface-light shrink-0">
                {/* Free用户10篇限制提示 */}
                {!checkingLimit && canCreate && !canCreate.canCreate && (
                  <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-2xl">
                    <div className="flex items-start gap-3">
                      <AlertCircle size={18} className="text-amber-500 shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <div className="text-sm font-bold text-amber-700">
                          {language === 'zh' ? '已达存储上限' : 'Storage Limit Reached'}
                        </div>
                        <div className="text-xs text-amber-600 mt-1">
                          {language === 'zh'
                            ? `免费用户最多存储 ${canCreate.maxCount} 篇会议（当前 ${canCreate.currentCount} 篇）。请删除旧会议或升级会员。`
                            : `Free users can store up to ${canCreate.maxCount} meetings (current: ${canCreate.currentCount}). Please delete old meetings or upgrade.`}
                        </div>
                        {onUpgrade && (
                          <button
                            onClick={onUpgrade}
                            className="mt-2 text-xs font-bold text-amber-700 hover:text-amber-900 underline"
                          >
                            {language === 'zh' ? '升级会员' : 'Upgrade Now'}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* ASR模式已统一为普通版，不再显示选择 */}

                {checkingLimit ? (
                  <div className="flex items-center justify-center gap-2 mb-5">
                    <Loader2 size={16} className="animate-spin text-ink-muted" />
                    <span className="text-[10px] text-ink-muted font-bold uppercase tracking-widest">
                      {language === 'zh' ? '检查中...' : 'Checking...'}
                    </span>
                  </div>
                ) : (
                  <div className="text-[10px] text-ink-muted flex items-center justify-center gap-2 font-bold uppercase tracking-widest mb-5">
                    <div className={`w-1.5 h-1.5 rounded-full ${fileId ? 'bg-brand-500 shadow-[0_0_8px_#84cc16] animate-pulse' : 'bg-surface-divider'}`}></div>
                    {t.newModal.autoDetect}
                  </div>
                )}

                <button
                    onClick={() => onStart({
                        scene: selectedScenario,
                        fileId: fileId || undefined,
                        industry: selectedScenario === 'finance' ? industry : undefined,
                        company: selectedScenario === 'finance' ? company : undefined,
                        context: content || undefined,
                    })}
                    disabled={!fileId || uploading || checkingLimit || !!(canCreate && !canCreate.canCreate)}
                    className={`w-full relative overflow-hidden h-14 rounded-2xl text-base font-bold shadow-xl transition-all duration-300 group flex items-center justify-center gap-3
                    ${fileId && !uploading && (!canCreate || canCreate.canCreate)
                        ? 'bg-brand-900 hover:bg-brand-800 text-white hover:shadow-2xl hover:-translate-y-1 active:translate-y-0 active:shadow-lg'
                        : 'bg-surface text-ink-muted cursor-not-allowed border border-surface-border'}`}
                >
                    <div className="absolute inset-0 bg-noise opacity-10 mix-blend-overlay"></div>
                    <span className="relative z-10">{uploading ? (convertingMsg || (language === 'zh' ? '上传中...' : 'Uploading...')) : t.newModal.startBtn}</span>
                    <ArrowRight size={20} className={`relative z-10 transition-transform stroke-[2.5] ${fileId && !uploading && (!canCreate || canCreate.canCreate) ? 'opacity-90 group-hover:translate-x-1.5' : 'opacity-30'}`} />
                </button>
            </div>
        </div>
      </div>
    </div>
  );
}

// Helper to handle scenario titles properly
function titleMap(id: string, lang: 'zh' | 'en') {
    const zh: Record<string, string> = { podcast: '播客', finance: '金融访谈', qa: '问答', course: '课程' };
    const en: Record<string, string> = { podcast: 'Podcast', finance: 'Financial Interview', qa: 'Q&A', course: 'Course' };
    return lang === 'zh' ? zh[id] : en[id];
}

// Scenario Card Component - Redesigned to be taller and fill available space
function RichScenarioCard({ title, subtitle, description, color, theme, selected, onClick }: any) {
    const getThemeColors = () => {
        switch(theme) {
            case 'emerald': return { bg: 'bg-emerald-50', border: 'border-emerald-100', text: 'text-emerald-700', fill: 'bg-emerald-200', iconBg: 'bg-emerald-100' };
            case 'blue': return { bg: 'bg-blue-50', border: 'border-blue-100', text: 'text-blue-700', fill: 'bg-blue-200', iconBg: 'bg-blue-100' };
            case 'amber': return { bg: 'bg-amber-50', border: 'border-amber-100', text: 'text-amber-700', fill: 'bg-amber-200', iconBg: 'bg-amber-100' };
            case 'purple': return { bg: 'bg-purple-50', border: 'border-purple-100', text: 'text-purple-700', fill: 'bg-purple-200', iconBg: 'bg-purple-100' };
            default: return { bg: 'bg-gray-50', border: 'border-gray-100', text: 'text-gray-700', fill: 'bg-gray-200', iconBg: 'bg-gray-100' };
        }
    }
    const themeCols = getThemeColors();

    return (
        <div 
            onClick={onClick}
            className={`
            relative rounded-modal transition-all duration-300 cursor-pointer overflow-visible group flex flex-col p-6 h-full
            ${selected 
                ? 'bg-white ring-2 ring-brand-900 shadow-xl z-10' 
                : 'bg-white border border-surface-border hover:border-surface-divider hover:shadow-lg hover:translate-y-[-1px]'}
        `}>
            {selected && (
                <div className="absolute -top-3 -right-3 w-8 h-8 rounded-full bg-brand-900 text-white flex items-center justify-center shadow-lg z-20 animate-in zoom-in duration-300">
                    <CheckCircle2 size={16} />
                </div>
            )}

            <div className="flex items-start gap-4 mb-4">
                <div className={`w-14 h-16 rounded-[22px] ${themeCols.bg} border ${themeCols.border} relative overflow-hidden shrink-0 flex flex-col p-3 shadow-inner group-hover:scale-105 transition-transform duration-500`}>
                    <div className={`h-3 w-full rounded-md ${color} mb-2 opacity-90`}></div>
                    <div className="space-y-1.5 flex-1">
                        <div className={`h-1 w-3/4 ${themeCols.fill} rounded-full`}></div>
                        <div className="h-0.5 w-full bg-white rounded-full opacity-60"></div>
                        <div className="h-0.5 w-full bg-white rounded-full opacity-60"></div>
                    </div>
                </div>

                <div className="flex-1 min-w-0 flex flex-col justify-center">
                    <h4 className={`text-lg font-serif font-bold mb-1 leading-tight ${selected ? 'text-brand-900' : 'text-prestige-900'}`}>
                        {title}
                    </h4>
                    <p className="text-[10px] font-black text-ink-muted tracking-[0.1em] uppercase opacity-90 whitespace-normal leading-relaxed">
                        {subtitle}
                    </p>
                </div>
            </div>

            <div className="mt-auto pt-4 border-t border-surface-hover/60">
                {/* Fixed: No truncation, font size increased for legibility */}
                <p className={`text-sm leading-relaxed font-medium ${selected ? 'text-ink-dark' : 'text-ink-muted'}`}>
                    {description}
                </p>
            </div>
        </div>
    )
}
